import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authService } from '../services';

const AuthContext = createContext();

const initialState = {
  user: null,
  token: null,
  loading: true,
  error: null,
  showPasswordResetModal: false,
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        loading: false,
        error: null,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        loading: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        loading: false,
        error: null,
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };
    case 'SHOW_PASSWORD_RESET_MODAL':
      return {
        ...state,
        showPasswordResetModal: true,
      };
    case 'HIDE_PASSWORD_RESET_MODAL':
      return {
        ...state,
        showPasswordResetModal: false,
      };
    default:
      return state;
  }
};

const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const initializeAuth = () => {
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');

      if (token && user) {
        if (isTokenExpired(token)) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          dispatch({ type: 'SET_LOADING', payload: false });
          return;
        }

        try {
          const parsedUser = JSON.parse(user);
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: {
              user: parsedUser,
              token,
            },
          });

          // Check if password reset is required on app initialization
          if (parsedUser.isforgotpassword === 1) {
            dispatch({ type: 'SHOW_PASSWORD_RESET_MODAL' });
          }
        } catch (error) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initializeAuth();

    const handleForceLogout = () => {
      dispatch({ type: 'LOGOUT' });
    };
    window.addEventListener('auth:logout', handleForceLogout);
    return () => window.removeEventListener('auth:logout', handleForceLogout);
  }, []);

  const login = async (credentials) => {
    try {
      dispatch({ type: 'LOGIN_START' });
      const response = await authService.login(credentials);
      const { token, user } = response.data.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { token, user },
      });

      // Check if password reset is required
      if (user.isforgotpassword === 1) {
        dispatch({ type: 'SHOW_PASSWORD_RESET_MODAL' });
      }

      return { success: true, user };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Login failed';
      dispatch({
        type: 'LOGIN_FAILURE',
        payload: errorMessage,
      });
      return { success: false, error: errorMessage, status: error.response?.status, network: !error.response };
    }
  };

  const googleLogin = async (idToken) => {
    try {
      dispatch({ type: 'LOGIN_START' });
      const response = await authService.googleLogin(idToken);
      const { token, user } = response.data.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      dispatch({ type: 'LOGIN_SUCCESS', payload: { token, user } });

      if (user.isforgotpassword === 1) {
        dispatch({ type: 'SHOW_PASSWORD_RESET_MODAL' });
      }

      return { success: true, user };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Google login failed';
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      return { success: false, error: errorMessage, status: error.response?.status, network: !error.response };
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      dispatch({ type: 'LOGOUT' });
    }
  };

  const updateUser = (userData) => {
    const updatedUser = { ...state.user, ...userData };
    localStorage.setItem('user', JSON.stringify(updatedUser));
    dispatch({
      type: 'UPDATE_USER',
      payload: userData,
    });
  };

  const hidePasswordResetModal = () => {
    // Only hide if password reset is no longer required
    if (state.user && state.user.isforgotpassword !== 1) {
      dispatch({ type: 'HIDE_PASSWORD_RESET_MODAL' });
    }
  };

  const handlePasswordResetSuccess = () => {
    // Update user to set isforgotpassword to 0
    updateUser({ isforgotpassword: 0 });
    dispatch({ type: 'HIDE_PASSWORD_RESET_MODAL' });
  };

  const value = {
    ...state,
    login,
    googleLogin,
    logout,
    updateUser,
    hidePasswordResetModal,
    handlePasswordResetSuccess,
    isAuthenticated: !!state.token,
    isAdmin: state.user?.usertype === 'ADMIN',
    isHR: state.user?.usertype === 'HR',
    isEmployee: state.user?.usertype === 'EMPLOYEE',
    isPeopleManager: state.user?.is_people_manager || false, // Expose manager status
    checkAuth: async () => { /* ... */ }, // Placeholder if needed, but the value is enough
    hasAssetAccess: state.user && state.user?.hasassetaccess === 1,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
